import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { Form, Formik } from "formik";
import {
  BookOpen,
  Check,
  Copy,
  KeyRound,
  Lock,
  Mail,
  Plug,
  Plus,
  Radio,
  RefreshCw,
  Send,
  Server,
  Signature,
  TriangleAlert,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Loader } from "@/components/common/loader";
import { PageHeader } from "@/components/common/page-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatShortDate } from "@/lib/format";
import { updateFirmSettings } from "@/features/settings/firm-api";
import { FIELD_LABEL } from "@/features/settings/components/settings-section";
import { SwitchField } from "@/features/settings/components/switch-field";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import type { EServicesStatus } from "@/features/eservices/types";
import { ApiError } from "@/lib/api";

const CONNECTOR_DOCS_URL = "https://docs.nsicapitals.com/connector";

const schema = Yup.object({
  eservicesDown: Yup.boolean(),
  eservicesMessage: Yup.string().max(2000, "Message is too long"),
  efilingEnabled: Yup.boolean(),
  efilingDown: Yup.boolean(),
  efilingMessage: Yup.string().max(2000, "Message is too long"),
});

interface IntegrationValues {
  eservicesDown: boolean;
  eservicesMessage: string;
  efilingEnabled: boolean;
  efilingDown: boolean;
  efilingMessage: string;
}

/**
 * Settings › Integrations — IRS e-Services / e-Filing channels, the firm-scoped
 * connector tokens, and the read-only env-managed provider/email config. Per-
 * practitioner CAF / e-Services identity is edited under Practitioners.
 */
export function IntegrationsTab() {
  const { settings, setSettings, loading, error, isAdmin } = useFirmSettings();
  const [status, setStatus] = useState<EServicesStatus | null>(null);
  const [tokenConfigured, setTokenConfigured] = useState<boolean | null>(null);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [efilingTokenConfigured, setEfilingTokenConfigured] = useState<boolean | null>(null);
  const [efilingToken, setEfilingToken] = useState<string | null>(null);

  useEffect(() => {
    import("@/features/eservices/connector-api")
      .then((m) => m.getConnectorTokenStatus())
      .then((r) => setTokenConfigured(r.configured))
      .catch(() => setTokenConfigured(null));
    import("@/features/efiling/connector-api")
      .then((m) => m.getConnectorTokenStatus())
      .then((configured) => setEfilingTokenConfigured(configured))
      .catch(() => setEfilingTokenConfigured(null));
    import("@/features/eservices/api")
      .then((m) => m.getStatus())
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  async function handleSubmit(values: IntegrationValues) {
    try {
      const updated = await updateFirmSettings({
        eservicesDown: values.eservicesDown,
        eservicesMessage: values.eservicesMessage,
        efilingEnabled: values.efilingEnabled,
        efilingDown: values.efilingDown,
        efilingMessage: values.efilingMessage,
      });
      setSettings(updated);
      toast.success("Integration settings saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save changes.");
    }
  }

  const mintToken = async () => {
    try {
      const m = await import("@/features/eservices/connector-api");
      const { token } = await m.mintConnectorToken();
      setNewToken(token);
      setTokenConfigured(true);
    } catch {
      toast.error("Could not mint a connector token.");
    }
  };

  const mintEfilingToken = async () => {
    try {
      const m = await import("@/features/efiling/connector-api");
      const token = await m.mintConnectorToken();
      setEfilingToken(token);
      setEfilingTokenConfigured(true);
    } catch {
      toast.error("Could not mint an e-Filing connector token.");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Integrations"
        subtitle="IRS e-Services and e-Filing channels, the firm-scoped tokens your out-of-band connector authenticates with, and the providers configured outside the app."
        actions={
          <Button asChild variant="outline" size="sm">
            <a href={CONNECTOR_DOCS_URL} target="_blank" rel="noreferrer">
              <BookOpen className="size-4" /> Connector docs
            </a>
          </Button>
        }
      />
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error ? (
        <p className="text-destructive text-sm">
          Couldn&apos;t load this section. Please refresh and try again.
        </p>
      ) : (
        settings && (
          <Formik<IntegrationValues>
            initialValues={{
              eservicesDown: settings.eservicesDown,
              eservicesMessage: settings.eservicesMessage,
              efilingEnabled: settings.efilingEnabled,
              efilingDown: settings.efilingDown,
              efilingMessage: settings.efilingMessage,
            }}
            enableReinitialize
            validationSchema={schema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, dirty, values }) => (
              <Form className="flex flex-col gap-6">
                <StatusStrip
                  eservicesDown={values.eservicesDown}
                  efilingEnabled={values.efilingEnabled}
                  efilingDown={values.efilingDown}
                  tokenConfigured={tokenConfigured}
                  efilingTokenConfigured={efilingTokenConfigured}
                />

                {/* Channels — e-Services + e-Filing */}
                <fieldset disabled={!isAdmin} className="contents">
                  <div className="grid items-start gap-6 lg:grid-cols-2">
                    <IntegrationCard
                      id="eservices"
                      icon={Radio}
                      iconWell="bg-accent text-info"
                      title="IRS e-Services"
                      subtitle="Transcript Delivery System (TDS) · pulls via out-of-band connector"
                      status={
                        values.eservicesDown ? (
                          <StatusPill tone="warn">Outage</StatusPill>
                        ) : (
                          <StatusPill tone="ok">Operational</StatusPill>
                        )
                      }
                    >
                      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
                        <dt className="text-muted-foreground">Queue</dt>
                        <dd className="m-0 font-semibold">
                          {status ? (
                            <>
                              {status.pendingMonitorCount} transcripts pending{" "}
                              <span className="text-muted-foreground font-normal">
                                · {values.eservicesDown ? "queueing paused" : "accepting new pulls"}
                              </span>
                            </>
                          ) : (
                            "—"
                          )}
                        </dd>
                        <dt className="text-muted-foreground">Last successful pull</dt>
                        <dd className="text-muted-foreground m-0 font-semibold">
                          {status
                            ? status.lastMonitorCheck
                              ? formatShortDate(status.lastMonitorCheck)
                              : "never"
                            : "—"}
                        </dd>
                        <dt className="text-muted-foreground">Session mode</dt>
                        <dd className="m-0">
                          <Chip>out-of-band</Chip>{" "}
                          <span className="text-muted-foreground">
                            · weekly auto-connect Mon 06:00
                          </span>
                        </dd>
                      </dl>
                      <Divider />
                      <SwitchField
                        name="eservicesDown"
                        label="Declare an e-Services outage"
                        hint="Shows a banner firm-wide, blocks new transcript queueing, and pauses monitoring until cleared."
                      />
                      <FormField
                        name="eservicesMessage"
                        label="Outage message"
                        placeholder="e.g. IRS TDS maintenance until Monday."
                        labelClassName={FIELD_LABEL}
                      />
                    </IntegrationCard>

                    <IntegrationCard
                      id="efiling"
                      icon={Send}
                      iconWell="bg-accent text-info"
                      title="IRS e-Filing"
                      subtitle="Submissions from a client's e-Filing tab · transmitted with the firm's IRS credentials"
                      status={
                        !values.efilingEnabled ? (
                          <StatusPill tone="off">Disabled</StatusPill>
                        ) : values.efilingDown ? (
                          <StatusPill tone="warn">Paused</StatusPill>
                        ) : (
                          <StatusPill tone="ok">Enabled</StatusPill>
                        )
                      }
                    >
                      <SwitchField
                        name="efilingEnabled"
                        label="Enable IRS e-Filing"
                        hint="Required before any client submission can be transmitted. Needs the e-Filing connector token below."
                        disabled={efilingTokenConfigured === false}
                      />
                      {efilingTokenConfigured === false && (
                        <div className="bg-warning-soft text-warning flex items-start gap-2.5 rounded-xl border border-current/20 p-3 text-xs leading-relaxed">
                          <TriangleAlert className="mt-px size-4 shrink-0" />
                          <span>
                            Blocked: generate the{" "}
                            <a href="#token-efile" className="font-semibold underline">
                              e-Filing connector token
                            </a>{" "}
                            first — the toggle unlocks once a token is active.
                          </span>
                        </div>
                      )}
                      <Divider />
                      <SwitchField
                        name="efilingDown"
                        label="e-Filing temporarily unavailable"
                        hint="Pauses new submissions and shows the message below on every e-Filing tab."
                      />
                      <FormField
                        name="efilingMessage"
                        label="Unavailable message"
                        placeholder="e.g. Connector maintenance until Monday."
                        labelClassName={FIELD_LABEL}
                      />
                    </IntegrationCard>
                  </div>
                </fieldset>

                {/* Connector tokens */}
                <div className="grid items-start gap-6 lg:grid-cols-2">
                  <TokenCard
                    id="token-pulls"
                    title="Connector token"
                    subtitle="Firm-scoped bearer token the browser extension / desktop agent uses to claim queued pulls"
                    prefix="nsi_conn_live_"
                    configured={tokenConfigured}
                    revealedToken={newToken}
                    onGenerate={mintToken}
                    isAdmin={isAdmin}
                    note={
                      <>
                        The full token is shown{" "}
                        <strong className="text-foreground font-semibold">once</strong> at
                        generation. Store it in the connector; rotating invalidates the previous
                        token after a 15-minute grace period.
                      </>
                    }
                    scopes={["pulls:claim", "transcripts:write", "mailbox:read"]}
                    endpoints={["POST /v1/connector/pulls/claim"]}
                    lastUsed="never · no connector has checked in"
                    expiry="90 days after issue · auto-reminder 7 days before"
                  />
                  <TokenCard
                    id="token-efile"
                    title="e-Filing connector token"
                    subtitle="Separate token to claim queued IRS submissions and post back acknowledgements"
                    prefix="nsi_efile_live_"
                    configured={efilingTokenConfigured}
                    revealedToken={efilingToken}
                    onGenerate={mintEfilingToken}
                    isAdmin={isAdmin}
                    note="Keep this separate from the pulls token so a compromised transcript connector can never transmit filings."
                    scopes={["submissions:claim", "acks:write"]}
                    endpoints={[
                      "POST /v1/connector/submissions/claim",
                      "POST /v1/connector/submissions/:id/ack",
                    ]}
                    lastUsed="never"
                  />
                </div>

                {/* Install + Providers */}
                <div className="grid items-start gap-6 lg:grid-cols-2">
                  <InstallConnectorCard />
                  <ProvidersCard />
                </div>

                {/* Save bar */}
                <Card className="shadow-card-hover flex flex-row flex-wrap items-center justify-between gap-3 p-4">
                  <span className="text-muted-foreground inline-flex items-center gap-2.5 text-[13px]">
                    <span
                      className={cn(
                        "size-2 rounded-full",
                        dirty ? "bg-warning" : "bg-muted-foreground/40",
                      )}
                    />
                    {dirty
                      ? "Unsaved changes"
                      : "No unsaved changes · tokens apply immediately when generated"}
                  </span>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" disabled className="gap-2">
                      <RefreshCw className="size-4" /> Run health check
                    </Button>
                    {isAdmin && (
                      <Button type="submit" variant="cta" disabled={isSubmitting || !dirty}>
                        <Check className="size-4" /> {isSubmitting ? "Saving…" : "Save"}
                      </Button>
                    )}
                  </div>
                </Card>
              </Form>
            )}
          </Formik>
        )
      )}
    </div>
  );
}

type IconType = ComponentType<{ className?: string }>;

/** Thin divider inside a card body. */
function Divider() {
  return <div className="bg-border h-px" />;
}

/** Monospace token/value chip. */
function Chip({ children }: { children: ReactNode }) {
  return (
    <code className="bg-muted text-foreground/80 rounded px-1.5 py-0.5 font-mono text-xs">
      {children}
    </code>
  );
}

type Tone = "ok" | "warn" | "off" | "info";

const PILL_TONE: Record<Tone, string> = {
  ok: "bg-success-soft text-success",
  warn: "bg-warning-soft text-warning",
  off: "bg-muted text-muted-foreground",
  info: "bg-info-soft text-info",
};

/** Soft status pill with a leading dot (inherits the pill's text colour). */
function StatusPill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
        PILL_TONE[tone],
      )}
    >
      <span className="size-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

/** White card with a bottom-bordered header (icon well + title/subtitle + status pill). */
function IntegrationCard({
  id,
  icon: Icon,
  iconWell,
  title,
  subtitle,
  status,
  children,
}: {
  id?: string;
  icon: IconType;
  iconWell: string;
  title: string;
  subtitle: string;
  status?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card id={id} className="scroll-mt-24 gap-0 overflow-hidden p-0">
      <div className="flex items-center justify-between gap-3 border-b px-5 py-4">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-sm",
              iconWell,
            )}
          >
            <Icon className="size-[18px]" />
          </span>
          <div className="flex flex-col">
            <h3 className="text-lg font-bold">{title}</h3>
            <p className="text-muted-foreground text-xs">{subtitle}</p>
          </div>
        </div>
        {status}
      </div>
      <div className="flex flex-col gap-4 px-5 py-4">{children}</div>
    </Card>
  );
}

/** Top-of-page integration status strip: one tile per channel/token/provider. */
function StatusStrip({
  eservicesDown,
  efilingEnabled,
  efilingDown,
  tokenConfigured,
  efilingTokenConfigured,
}: {
  eservicesDown: boolean;
  efilingEnabled: boolean;
  efilingDown: boolean;
  tokenConfigured: boolean | null;
  efilingTokenConfigured: boolean | null;
}) {
  const tokenTone = (configured: boolean | null): Tone =>
    configured == null ? "off" : configured ? "ok" : "warn";
  const tokenLabel = (configured: boolean | null) =>
    configured == null ? "—" : configured ? "Configured" : "Not configured";

  const tiles: { href: string; label: string; status: string; tone: Tone }[] = [
    {
      href: "#eservices",
      label: "IRS e-Services",
      status: eservicesDown ? "Outage" : "Operational",
      tone: eservicesDown ? "warn" : "ok",
    },
    {
      href: "#efiling",
      label: "IRS e-Filing",
      status: !efilingEnabled ? "Disabled" : efilingDown ? "Paused" : "Enabled",
      tone: !efilingEnabled ? "off" : efilingDown ? "warn" : "ok",
    },
    {
      href: "#token-pulls",
      label: "Connector token",
      status: tokenLabel(tokenConfigured),
      tone: tokenTone(tokenConfigured),
    },
    {
      href: "#token-efile",
      label: "e-Filing token",
      status: tokenLabel(efilingTokenConfigured),
      tone: tokenTone(efilingTokenConfigured),
    },
    { href: "#providers", label: "Providers", status: "Env-managed", tone: "info" },
  ];
  const textTone: Record<Tone, string> = {
    ok: "text-success",
    warn: "text-warning",
    off: "text-muted-foreground",
    info: "text-info",
  };
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {tiles.map((t) => (
        <a key={t.label} href={t.href}>
          <Card className="hover:shadow-card-hover h-full flex-col gap-2 p-4 transition-shadow">
            <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
              {t.label}
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 text-[13px] font-semibold",
                textTone[t.tone],
              )}
            >
              <span className="size-2 rounded-full bg-current" />
              {t.status}
            </span>
          </Card>
        </a>
      ))}
    </div>
  );
}

/** Firm-scoped connector token: masked dark bar + mint/rotate/revoke + metadata. */
function TokenCard({
  id,
  title,
  subtitle,
  prefix,
  configured,
  revealedToken,
  onGenerate,
  isAdmin,
  note,
  scopes,
  endpoints,
  lastUsed,
  expiry,
}: {
  id: string;
  title: string;
  subtitle: string;
  prefix: string;
  configured: boolean | null;
  revealedToken: string | null;
  onGenerate: () => void;
  isAdmin: boolean;
  note: ReactNode;
  scopes: string[];
  endpoints: string[];
  lastUsed: string;
  expiry?: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    if (!revealedToken) return;
    await navigator.clipboard.writeText(revealedToken);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <IntegrationCard
      id={id}
      icon={KeyRound}
      iconWell="bg-brand-panel text-white"
      title={title}
      subtitle={subtitle}
      status={
        configured ? (
          <StatusPill tone="ok">Configured</StatusPill>
        ) : (
          <StatusPill tone="warn">Not configured</StatusPill>
        )
      }
    >
      <div className="bg-brand-panel flex items-center gap-2 rounded-md py-2 pr-2 pl-3.5">
        <code className="text-brand-panel-muted min-w-0 flex-1 truncate font-mono text-[13px]">
          {revealedToken ? (
            revealedToken
          ) : (
            <>
              {prefix} ••••••••••••••••••••••••{" "}
              <span className="text-white/40">(no token issued)</span>
            </>
          )}
        </code>
        <button
          type="button"
          aria-label="Copy token"
          disabled={!revealedToken}
          onClick={copy}
          className="flex size-8 items-center justify-center rounded-sm bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-40 disabled:hover:bg-white/10"
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </button>
      </div>
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={onGenerate}
            className="bg-brand-panel gap-1.5 text-white hover:bg-brand-panel/90"
          >
            <Plus className="size-4" /> {configured ? "Rotate token" : "Generate token"}
          </Button>
          <Button type="button" variant="outline" disabled>
            Rotate
          </Button>
          <Button type="button" variant="outline" disabled>
            Revoke
          </Button>
        </div>
      )}
      <p className="text-muted-foreground text-xs leading-relaxed">{note}</p>
      <Divider />
      <dl className="grid grid-cols-[auto_1fr] items-start gap-x-4 gap-y-2 text-[13px]">
        <dt className="text-muted-foreground">Scopes</dt>
        <dd className="m-0 flex flex-wrap gap-1.5">
          {scopes.map((s) => (
            <Chip key={s}>{s}</Chip>
          ))}
        </dd>
        <dt className="text-muted-foreground">{endpoints.length > 1 ? "Endpoints" : "Endpoint"}</dt>
        <dd className="m-0 flex flex-col gap-0.5">
          {endpoints.map((e) => (
            <code key={e} className="font-mono text-xs">
              {e}
            </code>
          ))}
        </dd>
        <dt className="text-muted-foreground">Last used</dt>
        <dd className="text-muted-foreground m-0">{lastUsed}</dd>
        {expiry && (
          <>
            <dt className="text-muted-foreground">Expiry</dt>
            <dd className="m-0">{expiry}</dd>
          </>
        )}
      </dl>
    </IntegrationCard>
  );
}

/** Navy hero: how to install the out-of-band connector. */
function InstallConnectorCard() {
  const command = "nsi-connector login --token $NSI_CONNECTOR_TOKEN";
  const steps = [
    <>
      Install the <strong className="font-semibold">browser extension</strong> (Chrome / Edge) or
      the <strong className="font-semibold">desktop agent</strong> (macOS / Windows).
    </>,
    <>Paste the connector token when prompted, then (optionally) the e-Filing token.</>,
    <>Sign in to IRS e-Services once; the connector claims the queue and reports back here.</>,
  ];
  return (
    <div className="bg-brand-panel relative flex flex-col gap-3.5 overflow-hidden rounded-xl p-6 text-white">
      <div
        aria-hidden
        className="bg-primary/20 pointer-events-none absolute -top-20 -right-20 size-60 rounded-full blur-3xl"
      />
      <div className="relative flex items-center justify-between gap-3">
        <h3 className="text-lg font-bold">Install the out-of-band connector</h3>
        <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold">
          v2.4.1
        </span>
      </div>
      <p className="text-brand-panel-muted relative text-[13px] leading-relaxed">
        The connector runs on a practitioner's machine, signs in to IRS e-Services with their own
        credentials, and exchanges work with the platform using the tokens above — IRS passwords
        never touch our servers.
      </p>
      <ol className="relative flex list-none flex-col gap-2">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-2.5 text-[13px]">
            <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-white/15 text-[11px] font-bold">
              {i + 1}
            </span>
            <span className="text-brand-panel-muted">{s}</span>
          </li>
        ))}
      </ol>
      <div className="relative flex items-center gap-2 rounded-md bg-black/30 py-2 pr-2 pl-3.5">
        <code className="text-brand-panel-muted min-w-0 flex-1 truncate font-mono text-xs">
          <span className="text-white/40">$</span> {command}
        </code>
        <button
          type="button"
          aria-label="Copy command"
          onClick={() => {
            navigator.clipboard.writeText(command);
            toast.success("Copied to clipboard");
          }}
          className="flex size-8 items-center justify-center rounded-sm bg-white/10 text-white hover:bg-white/20"
        >
          <Copy className="size-4" />
        </button>
      </div>
      <div className="relative flex flex-wrap gap-2">
        <Button asChild className="bg-white text-brand-panel hover:bg-white/90">
          <a href={CONNECTOR_DOCS_URL} target="_blank" rel="noreferrer">
            Get browser extension
          </a>
        </Button>
        <Button
          asChild
          variant="outline"
          className="border-white/25 bg-white/10 text-white hover:bg-white/20 hover:text-white"
        >
          <a href={CONNECTOR_DOCS_URL} target="_blank" rel="noreferrer">
            Download desktop agent
          </a>
        </Button>
      </div>
    </div>
  );
}

/** Read-only, env-managed provider & email configuration. */
function ProvidersCard() {
  const rows: {
    icon: IconType;
    name: string;
    desc: string;
    env: string;
    tone: Tone;
    status: string;
  }[] = [
    {
      icon: Signature,
      name: "E-signature provider",
      desc: "DigiSigner or DocuSign",
      env: "ESIGN_PROVIDER=digisigner",
      tone: "ok",
      status: "Set",
    },
    {
      icon: KeyRound,
      name: "E-signature API key",
      desc: "Secret · never displayed",
      env: "ESIGN_API_KEY=••••",
      tone: "ok",
      status: "Set",
    },
    {
      icon: Mail,
      name: "Transactional email",
      desc: "Postmark · portal invites, notifications",
      env: "POSTMARK_SERVER_TOKEN=••••",
      tone: "ok",
      status: "Set",
    },
    {
      icon: Server,
      name: "Sender domain",
      desc: "DKIM / return-path verified at Postmark",
      env: "MAIL_FROM=no-reply@[domain]",
      tone: "warn",
      status: "Verify",
    },
  ];
  const textTone: Record<Tone, string> = {
    ok: "text-success",
    warn: "text-warning",
    off: "text-muted-foreground",
    info: "text-info",
  };
  return (
    <IntegrationCard
      id="providers"
      icon={Plug}
      iconWell="bg-accent text-info"
      title="Providers & email"
      subtitle="Configured by environment variables and secrets at deploy time — read-only here"
      status={
        <span className="bg-info-soft text-info inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold">
          <Lock className="size-3" /> Env-managed
        </span>
      }
    >
      <ul className="-mx-1 flex list-none flex-col gap-0.5">
        {rows.map((r) => (
          <li key={r.name} className="flex flex-wrap items-center gap-3 rounded-md p-2">
            <span className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
              <r.icon className="size-4" />
            </span>
            <span className="flex min-w-0 flex-1 basis-44 flex-col">
              <span className="font-semibold">{r.name}</span>
              <span className="text-muted-foreground text-xs">{r.desc}</span>
            </span>
            <Chip>{r.env}</Chip>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 text-xs font-semibold",
                textTone[r.tone],
              )}
            >
              <span className="size-1.5 rounded-full bg-current" />
              {r.status}
            </span>
          </li>
        ))}
      </ul>
      <Divider />
      <p className="text-muted-foreground text-xs leading-relaxed">
        Per-practitioner CAF numbers and e-Services identities are edited under{" "}
        <Link to="/practitioners" className="text-info font-semibold hover:underline">
          Practitioners
        </Link>
        . To change a provider, update the deployment secrets and redeploy.
      </p>
    </IntegrationCard>
  );
}
